import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { largeNumberFormatter } from 'shared/base';

import {
  BAR_CHART_TOOLTIP_BUFFER_WIDTH,
  OTHER_LABEL,
  TOOLTIP_MAX_COL_COUNT,
  TOTAL_LABEL,
} from '../../constants';
import { BarChartData } from '../../types';

export const BarChartTooltipContent = ({
  entryLabel = 'Count',
  maxLabelWidth,
  secondaryData,
  tooltipData,
}: {
  entryLabel?: string;
  maxLabelWidth: number;
  secondaryData?: BarChartData;
  tooltipData: BarChartData[number] | null;
}) => {
  const { palette } = useTheme();
  const useColumns = secondaryData && secondaryData.length > TOOLTIP_MAX_COL_COUNT;

  if (!tooltipData) {
    return null;
  }

  if (tooltipData.name === OTHER_LABEL && secondaryData) {
    const otherTotal = secondaryData?.reduce((acc, curr) => acc + curr.value, 0) ?? 0;
    const items = [...(secondaryData ?? []), { name: TOTAL_LABEL, value: otherTotal }];

    const colWidth = (maxLabelWidth + BAR_CHART_TOOLTIP_BUFFER_WIDTH) * 1.5;
    const columns = Math.ceil(secondaryData.length / 20);

    return (
      <Box maxWidth={colWidth * columns} p={1.5}>
        <Typography color="text.secondary" mb={0.5} variant="body2">
          {tooltipData.name}
        </Typography>
        <Box
          display={useColumns ? 'flex' : 'block'}
          flexWrap="wrap"
          height="100%"
          justifyContent="space-between"
        >
          {items?.map(({ name, value }) => {
            const isTotal = name === TOTAL_LABEL;

            return (
              <Box
                key={name}
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                sx={{
                  borderTop: isTotal ? `1px solid ${palette.divider}` : 'none',
                  mt: isTotal ? 1 : 0,
                  pt: isTotal ? 1 : 0,
                  width: useColumns && !isTotal ? `calc(90% / ${columns})` : '100%',
                }}
              >
                <Typography
                  color={name === OTHER_LABEL ? palette.text.secondary : palette.text.primary}
                  noWrap={true}
                  variant="body2"
                >
                  {name}
                </Typography>

                <Typography color="text.primary" pl={3} variant="body2Bold">
                  {largeNumberFormatter.format(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  } else {
    return (
      <Box p={1}>
        <Typography color="text.secondary" mb={0.5} variant="body2">
          {tooltipData.name}
        </Typography>
        <Stack alignItems="center" direction="row" gap={1}>
          <Box
            sx={{
              backgroundColor: tooltipData.color ?? palette.primary.dark,
              borderRadius: '2px',
              height: '14px',
              width: '7px',
            }}
          />
          <Typography color="text.primary" noWrap={true} variant="caption">
            {entryLabel}
          </Typography>
          <Typography color="text.primary" sx={{ fontWeight: 'bold' }} variant="caption">
            {largeNumberFormatter.format(tooltipData.value)}
          </Typography>
        </Stack>
      </Box>
    );
  }
};
